import * as PIXI from "pixi.js"
import Model from "../model/model"
import { HexColor, Idea, Location, ideaHeight, ideaWidth } from "../model/types"
import { defaultIdeaColourResolver, type IdeaColourResolver } from "./idea_colour"
import { type CameraState } from "./coords"
import {
  connectionCurve,
  ideaCenter,
  ideaRectangle,
  QuadraticCurve,
  Rectangle,
  rectangleInLocalCoordinates,
} from "./geometry"
import { BOX_WIDTH } from "./grid"

const GRID_COLOUR = 0xeaeaea
const DARK_GRID_COLOUR = 0x222222
const BACKGROUND_COLOUR = 0xf9f8f4
const DARK_BACKGROUND_COLOUR = 0x101319
const OPEN_COLOUR = 0x000000
const DARK_OPEN_COLOUR = 0xffffff
const HIGHLIGHT_WIDTH = 2
const IDEA_CORNER_RADIUS = 3
const IDEA_BORDER_WIDTH = 1
const IDEA_BORDER_ALPHA = 0.4

type BoxEdge = "top" | "right" | "bottom" | "left"
type GridCellOccupancyResolver = (location: Location) => boolean
type NeighbourState = {
  left: boolean
  right: boolean
  top: boolean
  bottom: boolean
}
type RoundedCorners = {
  topLeft: boolean
  topRight: boolean
  bottomRight: boolean
  bottomLeft: boolean
}

const gridCellKey = ({ gx, gy }: Location): string => `${gx},${gy}`

const emptyGridCellOccupancyResolver: GridCellOccupancyResolver = () => false

const createOccupiedGridCellResolver = (ideas: Iterable<Idea>): GridCellOccupancyResolver => {
  const occupiedCells = new Set<string>()

  for (const idea of ideas) {
    const { gx, gy } = idea.location
    for (let dx = 0; dx < ideaWidth(idea); dx++)
      for (let dy = 0; dy < ideaHeight(idea); dy++)
        occupiedCells.add(gridCellKey({ gx: gx + dx, gy: gy + dy }))
  }

  return (location: Location): boolean => occupiedCells.has(gridCellKey(location))
}

const hexColourToRgb = (colour: HexColor): { r: number; g: number; b: number } => ({
  r: parseInt(colour.slice(1, 3), 16),
  g: parseInt(colour.slice(3, 5), 16),
  b: parseInt(colour.slice(5, 7), 16),
})

const rgbToPixiColour = ({ r, g, b }: { r: number; g: number; b: number }): number =>
  (r << 16) + (g << 8) + b

const blendChannel = (from: number, to: number, amount: number): number =>
  Math.round(from + (to - from) * amount)

const borderColourForFill = (colour: HexColor): number => {
  const { r, g, b } = hexColourToRgb(colour)
  const brightness = Math.sqrt(0.299 * r * r + 0.587 * g * g + 0.114 * b * b)
  const target = brightness < 140 ? 255 : 0
  const amount = brightness < 140 ? 0.28 : 0.22

  return rgbToPixiColour({
    r: blendChannel(r, target, amount),
    g: blendChannel(g, target, amount),
    b: blendChannel(b, target, amount),
  })
}

const fillHighlightRect = (
  graphic: PIXI.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  colour: number,
): void => {
  graphic.lineStyle(0)
  graphic.beginFill(colour, 1)
  graphic.drawRect(x, y, width, height)
  graphic.endFill()
}

const createGrid = (_isDarkMode: boolean): PIXI.Graphics => {
  const grid = new PIXI.Graphics()
  return grid
}

const renderGridToGraphic = (
  grid: PIXI.Graphics,
  isDarkMode: boolean,
  { pan_x, pan_y, scale }: CameraState,
  screenWidth: number,
  screenHeight: number,
): void => {
  const safeScale = scale || 1
  const left = (0 - pan_x) / safeScale
  const top = (0 - pan_y) / safeScale
  const right = (screenWidth - pan_x) / safeScale
  const bottom = (screenHeight - pan_y) / safeScale
  const margin = BOX_WIDTH * 2
  const gridLeft = Math.floor((left - margin) / BOX_WIDTH) * BOX_WIDTH
  const gridTop = Math.floor((top - margin) / BOX_WIDTH) * BOX_WIDTH
  const gridRight = Math.ceil((right + margin) / BOX_WIDTH) * BOX_WIDTH
  const gridBottom = Math.ceil((bottom + margin) / BOX_WIDTH) * BOX_WIDTH

  grid.clear()
  grid.lineStyle(1, isDarkMode ? DARK_GRID_COLOUR : GRID_COLOUR, 1)

  for (let x = gridLeft; x <= gridRight; x += BOX_WIDTH) {
    grid.moveTo(x, gridTop)
    grid.lineTo(x, gridBottom)
  }

  for (let y = gridTop; y <= gridBottom; y += BOX_WIDTH) {
    grid.moveTo(gridLeft, y)
    grid.lineTo(gridRight, y)
  }
}

const drawHighlightEdgeTo = (
  graphic: PIXI.Graphics,
  edge: BoxEdge,
  gx: number,
  gy: number,
  width: number,
  height: number,
  colour: number,
): void => {
  const left = gx * BOX_WIDTH
  const top = gy * BOX_WIDTH
  const right = (gx + width) * BOX_WIDTH
  const bottom = (gy + height) * BOX_WIDTH
  const halfWidth = HIGHLIGHT_WIDTH / 2

  switch (edge) {
    case "top":
      fillHighlightRect(
        graphic,
        left - halfWidth,
        top - halfWidth,
        width * BOX_WIDTH + HIGHLIGHT_WIDTH,
        HIGHLIGHT_WIDTH,
        colour,
      )
      break
    case "right":
      fillHighlightRect(
        graphic,
        right - halfWidth,
        top - halfWidth,
        HIGHLIGHT_WIDTH,
        height * BOX_WIDTH + HIGHLIGHT_WIDTH,
        colour,
      )
      break
    case "bottom":
      fillHighlightRect(
        graphic,
        left - halfWidth,
        bottom - halfWidth,
        width * BOX_WIDTH + HIGHLIGHT_WIDTH,
        HIGHLIGHT_WIDTH,
        colour,
      )
      break
    case "left":
      fillHighlightRect(
        graphic,
        left - halfWidth,
        top - halfWidth,
        HIGHLIGHT_WIDTH,
        height * BOX_WIDTH + HIGHLIGHT_WIDTH,
        colour,
      )
      break
  }
}

const drawBoxHighlightToAt = (
  graphic: PIXI.Graphics,
  gx: number,
  gy: number,
  width: number,
  height: number,
  colour: number,
): void => {
  drawHighlightEdgeTo(graphic, "top", gx, gy, width, height, colour)
  drawHighlightEdgeTo(graphic, "right", gx, gy, width, height, colour)
  drawHighlightEdgeTo(graphic, "bottom", gx, gy, width, height, colour)
  drawHighlightEdgeTo(graphic, "left", gx, gy, width, height, colour)
}

const drawBoxHighlightTo = (
  graphic: PIXI.Graphics,
  width: number,
  height: number,
  colour: number,
): void => {
  drawBoxHighlightToAt(graphic, 0, 0, width, height, colour)
}

const renderOpenSquareTo = (graphic, isDarkMode, width, height) => {
  graphic.clear()
  graphic.beginFill(0x8888ff, 0.5)
  graphic.drawRect(0, 0, width * BOX_WIDTH, height * BOX_WIDTH)
  graphic.endFill()
  drawBoxHighlightTo(graphic, width, height, isDarkMode ? DARK_OPEN_COLOUR : OPEN_COLOUR)
}

const createLassoSquare = (): PIXI.Graphics => {
  let result: PIXI.Graphics = new PIXI.Graphics()
  result.renderable = false
  return result
}

const renderHomeDotToGraphic = (graphic: PIXI.Graphics, isDarkMode: boolean): void => {
  graphic.clear()
  graphic.beginFill(isDarkMode ? DARK_OPEN_COLOUR : OPEN_COLOUR)
  graphic.drawCircle(0, 0, 3)
  graphic.endFill()
  graphic.x = 0
  graphic.y = 0
}

const createHomeDot = (isDarkMode) => {
  let result = new PIXI.Graphics()
  renderHomeDotToGraphic(result, isDarkMode)
  return result
}

const renderBackgroundToGraphic = (
  background: PIXI.Graphics,
  isDarkMode: boolean,
  appWidth: number,
): void => {
  background.clear()
  background.beginFill(isDarkMode ? DARK_BACKGROUND_COLOUR : BACKGROUND_COLOUR)
  background.drawRect(0, 0, appWidth * 100, appWidth * 100)
  background.endFill()
  background.x = -1 * appWidth * 50
  background.y = -1 * appWidth * 50
}

const createBackground = (
  isDarkMode: boolean,
  appWidth: number,
  appHeight: number,
): PIXI.Graphics => {
  let background = new PIXI.Graphics()
  renderBackgroundToGraphic(background, isDarkMode, appWidth)
  return background
}

const neighbourStateForIdea = (
  idea: Idea,
  isGridCellOccupied: GridCellOccupancyResolver,
): NeighbourState => {
  const { gx, gy } = idea.location
  return {
    left: isGridCellOccupied({ gx: gx - 1, gy }),
    right: isGridCellOccupied({ gx: gx + 1, gy }),
    top: isGridCellOccupied({ gx, gy: gy - 1 }),
    bottom: isGridCellOccupied({ gx, gy: gy + 1 }),
  }
}

const roundedCornersForIdea = ({
  left,
  right,
  top,
  bottom,
}: NeighbourState): RoundedCorners => {
  return {
    topLeft: !left && !top,
    topRight: !right && !top,
    bottomRight: !right && !bottom,
    bottomLeft: !left && !bottom,
  }
}

const hasRoundedCorner = ({
  topLeft,
  topRight,
  bottomRight,
  bottomLeft,
}: RoundedCorners): boolean => topLeft || topRight || bottomRight || bottomLeft

const drawIdeaBorder = (
  graphic: PIXI.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  colour: HexColor,
  neighbours: NeighbourState,
  roundedCorners: RoundedCorners,
) => {
  const halfWidth = IDEA_BORDER_WIDTH / 2
  const left = x + halfWidth
  const top = y + halfWidth
  const right = x + width - halfWidth
  const bottom = y + height - halfWidth
  const radius = Math.max(
    0,
    Math.min(IDEA_CORNER_RADIUS, width / 2, height / 2) - halfWidth,
  )
  const topLeftRadius = roundedCorners.topLeft ? radius : 0
  const topRightRadius = roundedCorners.topRight ? radius : 0
  const bottomRightRadius = roundedCorners.bottomRight ? radius : 0
  const bottomLeftRadius = roundedCorners.bottomLeft ? radius : 0

  graphic.lineStyle(IDEA_BORDER_WIDTH, borderColourForFill(colour), IDEA_BORDER_ALPHA)

  if (!neighbours.top) {
    graphic.moveTo(left + topLeftRadius - (neighbours.left ? halfWidth : 0), top)
    graphic.lineTo(right - topRightRadius + (neighbours.right ? halfWidth : 0), top)
  }
  if (roundedCorners.topRight) {
    graphic.moveTo(right - radius, top)
    graphic.quadraticCurveTo(right, top, right, top + radius)
  }
  if (!neighbours.right) {
    graphic.moveTo(right, top + topRightRadius - (neighbours.top ? halfWidth : 0))
    graphic.lineTo(right, bottom - bottomRightRadius + (neighbours.bottom ? halfWidth : 0))
  }
  if (roundedCorners.bottomRight) {
    graphic.moveTo(right, bottom - radius)
    graphic.quadraticCurveTo(right, bottom, right - radius, bottom)
  }
  if (!neighbours.bottom) {
    graphic.moveTo(right - bottomRightRadius + (neighbours.right ? halfWidth : 0), bottom)
    graphic.lineTo(left + bottomLeftRadius - (neighbours.left ? halfWidth : 0), bottom)
  }
  if (roundedCorners.bottomLeft) {
    graphic.moveTo(left + radius, bottom)
    graphic.quadraticCurveTo(left, bottom, left, bottom - radius)
  }
  if (!neighbours.left) {
    graphic.moveTo(left, bottom - bottomLeftRadius + (neighbours.bottom ? halfWidth : 0))
    graphic.lineTo(left, top + topLeftRadius - (neighbours.top ? halfWidth : 0))
  }
  if (roundedCorners.topLeft) {
    graphic.moveTo(left, top + radius)
    graphic.quadraticCurveTo(left, top, left + radius, top)
  }
}

const drawIdeaSquare = (
  graphic: PIXI.Graphics,
  rectangle: Rectangle,
  colour: HexColor,
  neighbours: NeighbourState,
) => {
  const x = rectangle.gx * BOX_WIDTH
  const y = rectangle.gy * BOX_WIDTH
  const width = rectangle.width * BOX_WIDTH
  const height = rectangle.height * BOX_WIDTH
  const right = x + width
  const bottom = y + height
  const radius = Math.min(IDEA_CORNER_RADIUS, width / 2, height / 2)
  const roundedCorners = roundedCornersForIdea(neighbours)

  graphic.lineStyle(0)
  graphic.beginFill(colour)
  if (!hasRoundedCorner(roundedCorners) || radius <= 0) {
    graphic.drawRect(x, y, width, height)
    graphic.endFill()
    drawIdeaBorder(graphic, x, y, width, height, colour, neighbours, roundedCorners)
    return
  }

  const topLeftRadius = roundedCorners.topLeft ? radius : 0
  const topRightRadius = roundedCorners.topRight ? radius : 0
  const bottomRightRadius = roundedCorners.bottomRight ? radius : 0
  const bottomLeftRadius = roundedCorners.bottomLeft ? radius : 0

  graphic.moveTo(x + topLeftRadius, y)
  graphic.lineTo(right - topRightRadius, y)
  if (roundedCorners.topRight) graphic.quadraticCurveTo(right, y, right, y + radius)
  else graphic.lineTo(right, y)

  graphic.lineTo(right, bottom - bottomRightRadius)
  if (roundedCorners.bottomRight) graphic.quadraticCurveTo(right, bottom, right - radius, bottom)
  else graphic.lineTo(right, bottom)

  graphic.lineTo(x + bottomLeftRadius, bottom)
  if (roundedCorners.bottomLeft) graphic.quadraticCurveTo(x, bottom, x, bottom - radius)
  else graphic.lineTo(x, bottom)

  graphic.lineTo(x, y + topLeftRadius)
  if (roundedCorners.topLeft) graphic.quadraticCurveTo(x, y, x + radius, y)
  else graphic.lineTo(x, y)

  graphic.endFill()
  drawIdeaBorder(graphic, x, y, width, height, colour, neighbours, roundedCorners)
}

const drawGoalCircle = (graphic: PIXI.Graphics, rectangle: Rectangle, colour: HexColor) => {
  const center = {
    x: rectangle.gx * BOX_WIDTH + (BOX_WIDTH * rectangle.width) / 2,
    y: rectangle.gy * BOX_WIDTH + (BOX_WIDTH * rectangle.height) / 2,
  }
  const radius = (BOX_WIDTH * rectangle.width) / 2

  graphic.lineStyle(0)
  graphic.beginFill(colour)
  graphic.drawCircle(center.x, center.y, radius)
  graphic.endFill()
  graphic.lineStyle(IDEA_BORDER_WIDTH, borderColourForFill(colour), IDEA_BORDER_ALPHA)
  graphic.drawCircle(center.x, center.y, radius - IDEA_BORDER_WIDTH / 2)
}

const renderIdeaShapeToGraphic = (
  graphic: PIXI.Graphics,
  idea: Idea,
  originX: number = 0,
  originY: number = 0,
  ideaColourResolver: IdeaColourResolver = defaultIdeaColourResolver,
  isGridCellOccupied: GridCellOccupancyResolver = emptyGridCellOccupancyResolver,
) => {
  const rectangle = rectangleInLocalCoordinates(ideaRectangle(idea), { gx: originX, gy: originY })
  const colour = ideaColourResolver(idea)
  if (idea.type == "idea") {
    drawIdeaSquare(graphic, rectangle, colour, neighbourStateForIdea(idea, isGridCellOccupied))
  } else {
    drawGoalCircle(graphic, rectangle, colour)
  }
}

const draw_connection_curve = (graphic: PIXI.Graphics, curve: QuadraticCurve, colour: HexColor) => {
  graphic.lineStyle(3, colour, 1)
  graphic.moveTo(curve.from.gx * BOX_WIDTH, curve.from.gy * BOX_WIDTH)
  graphic.quadraticCurveTo(
    curve.control.gx * BOX_WIDTH,
    curve.control.gy * BOX_WIDTH,
    curve.to.gx * BOX_WIDTH,
    curve.to.gy * BOX_WIDTH,
  )
}

const renderIdeaConnectionsToGraphic = (
  connectionsGraphic: PIXI.Graphics,
  idea: Idea,
  model: Model,
  ideaColourResolver: IdeaColourResolver = defaultIdeaColourResolver,
) => {
  if (idea.connections.length) {
    idea.connections.forEach((toIdeaID) => {
      const toIdea = model.fetchIdea(toIdeaID)
      const curve = connectionCurve(ideaCenter(idea), ideaCenter(toIdea))
      draw_connection_curve(connectionsGraphic, curve, ideaColourResolver(idea))
    })
  }
}

const renderIdeaToGraphic = (
  graphic: PIXI.Graphics,
  connectionsGraphic: PIXI.Graphics,
  idea: Idea,
  model: Model,
  originX: number = 0,
  originY: number = 0,
  ideaColourResolver: IdeaColourResolver = defaultIdeaColourResolver,
  isGridCellOccupied: GridCellOccupancyResolver = emptyGridCellOccupancyResolver,
) => {
  renderIdeaShapeToGraphic(graphic, idea, originX, originY, ideaColourResolver, isGridCellOccupied)
  renderIdeaConnectionsToGraphic(connectionsGraphic, idea, model, ideaColourResolver)
}

export {
  createOccupiedGridCellResolver,
  createBackground,
  createGrid,
  createHomeDot,
  createLassoSquare,
  drawBoxHighlightTo,
  drawBoxHighlightToAt,
  drawHighlightEdgeTo,
  renderGridToGraphic,
  renderBackgroundToGraphic,
  renderHomeDotToGraphic,
  renderIdeaConnectionsToGraphic,
  renderIdeaShapeToGraphic,
  renderIdeaToGraphic,
  renderOpenSquareTo,
}
